import { Box, Button, Checkbox, Stack } from "@mui/material";
import React, { useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { TriggeredHook } from "sonobello.utilities.react";

import { LeadResult } from "../../../../dtos/LeadResult";
import { Step } from "../../../../types/Step";
import { isMobileView } from "../../../../utils/Constants";
import { IFlowStepProps } from "../../../Types/IFlow";
import StepWrapper, { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import IMedicalFormElementProps from "../Types/IMedicalFormElement";
import IMedicalQualification from "../Types/IMedicalQualification";
import IMedicalQuestionnaireCompleteEvaluator from "../Types/IMedicalQualificationCompleteEvaluator";
import IMedicalQualificationNonCandidateEvaluator from "../Types/IMedicalQualificationNonCandidateEvaluator";
import { IMedicalStepProps } from "./FormSeriesMedicalStep";

// Question IDs for top-level (parent) questions only — these are the ones "No to all" targets.
const TOP_LEVEL_IDS = [
  "generalMedicalQuestions.dialysis",
  "generalMedicalQuestions.oxygen",
  "generalMedicalQuestions.organTransplant",
  "generalMedicalQuestions.bleedingDisorder",
  "generalMedicalQuestions.bloodClot",
  "pacemakerMedicalQuestions.pacemaker",
  "radiationMedicalQuestions.cancer",
  "diabeticMedicalQuestions.diabetic"
];

// Child question IDs that should be cleared when all parents are set to No.
const CHILD_IDS = [
  "pacemakerMedicalQuestions.defibrillator",
  "radiationMedicalQuestions.chemo",
  "radiationMedicalQuestions.cancerChemo"
];

const BUTTON_BANNER_HEIGHT = 72;

/** The configuration for the no-to-all medical step. */
export interface INoToAllMedicalStepConfig {
  /** Evaluates the full form for completeness and non-candidacy. */
  medicalQualificationEvaluator: IMedicalQualificationNonCandidateEvaluator & IMedicalQuestionnaireCompleteEvaluator;
  /** The step to navigate to after the user submits and is not a non-candidate. */
  nextStep: Step;
  /** The question components to render. */
  FormQuestions: React.FC<IMedicalFormElementProps>[];
  /** A hook to be called when the user is disqualified as a non-candidate. */
  useCreateNonCandidateLeadResult: TriggeredHook<LeadResult, undefined, boolean>;
}

// ─── sub-component ─────────────────────────────────────────────────────────────

interface INoToAllRowProps {
  /** Whether all top-level questions are currently answered No. */
  isAllNo: boolean;
  /** Called when the button is clicked. */
  onNoToAll: () => void;
}

/** A right-aligned button with a checkbox indicator rendered inside its border. */
const NoToAllRow: React.FC<INoToAllRowProps> = ({ isAllNo, onNoToAll }) => (
  <Box display="flex" justifyContent="flex-end">
    <Button variant="outlined" size="small" onClick={onNoToAll}>
      <Checkbox
        checked={isAllNo}
        onChange={() => {}}
        size="small"
        disableRipple
        tabIndex={-1}
        sx={{ p: 0, mr: 0.5, pointerEvents: "none" }}
      />
      No to all
    </Button>
  </Box>
);

// ─── main step component ────────────────────────────────────────────────────────

/** All questions on one page, identical to the `CombinedPage` variant, with a **No to all** button and
 * checkbox indicator added at both the top and bottom of the question list.
 *
 * - Clicking **No to all** sets every top-level question to `false` and clears all child answers.
 * - Clicking the **top** button also scrolls to the bottom of the question content (after collapse
 *   animations settle) so the user can see the result.
 * - The checkbox next to each button shows a check when all top-level answers are currently `false`.
 */
const NoToAllMedicalStep: React.FC<IMedicalStepProps & INoToAllMedicalStepConfig & IStepWrapperButtonProps> = ({
  medicalQualificationEvaluator,
  nextStep,
  router,
  value,
  onMedicalNoncandidateDisqualification,
  setMedicalConclusions,
  setMedicalFormState,
  FormQuestions,
  useCreateNonCandidateLeadResult,
  NextButtonMobile,
  NextButtonDesktop
}) => {
  const isMobile = isMobileView();
  const flowStepProps = useOutletContext<IFlowStepProps>();
  const { execute: createNonCandidateLeadResult, result, isLoading } = useCreateNonCandidateLeadResult();

  useEffect(() => {
    if (result) router.goToNonCandidateView();
  }, [result]);

  const isAllNo = TOP_LEVEL_IDS.every(id => value[id] === false);

  const handleNoToAll = (scrollToBottom: boolean) => {
    const newState: IMedicalQualification = { ...value };
    TOP_LEVEL_IDS.forEach(id => { newState[id] = false; });
    CHILD_IDS.forEach(id => { newState[id] = null; });
    setMedicalFormState(newState);
    setMedicalConclusions({
      hasPacemakerWithoutDefibrillator: false,
      hasRadiationProtectedAreas: false,
      hasDiabetes: false
    });

    if (scrollToBottom) {
      // Wait for MUI Collapse animations (~300 ms) to finish before scrolling.
      setTimeout(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
      }, 350);
    }
  };

  const handleNext = () => {
    if (medicalQualificationEvaluator.isNonCandidate(value)) {
      onMedicalNoncandidateDisqualification();
      createNonCandidateLeadResult();
    } else {
      flowStepProps.setStep(nextStep);
    }
  };

  return (
    <StepWrapper
      NextButtonMobile={NextButtonMobile}
      NextButtonDesktop={NextButtonDesktop}
      nextButtonLoading={isLoading}
      onNext={handleNext}
      disabled={isLoading || !medicalQualificationEvaluator.isComplete(value)}
    >
      <Stack spacing={1.5}>
        <NoToAllRow isAllNo={isAllNo} onNoToAll={() => handleNoToAll(true)} />
        <div>
          <Stack spacing={1.5}>
            {FormQuestions.map((Question, index) => (
              <Question
                key={index}
                value={value}
                onChange={s => setMedicalFormState(o => ({ ...o, ...s }))}
                isMobile={isMobile}
                setMedicalConclusions={setMedicalConclusions}
                setMedicalFormState={setMedicalFormState}
              />
            ))}
          </Stack>
        </div>
        <Box sx={{ pb: "72px" }}>
          <NoToAllRow isAllNo={isAllNo} onNoToAll={() => handleNoToAll(false)} />
        </Box>
      </Stack>
    </StepWrapper>
  );
};

export default NoToAllMedicalStep;
